import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { hqAuthService } from '../services/hqAuthService';
import { useHqIdleLogout } from '../hooks/useHqIdleLogout';
import { hqErrorReportService } from '../services/hqErrorReportService';
import hqSupportService from '../services/hqSupportService';
import hqSignupService from '../services/hqSignupService';
import hqConsultationService from '../services/hqConsultationService';
import ChatWidget from '../../../components/chatbot/ChatWidget';

// hq.newattend.com 서브도메인이면 path prefix 없음, 그 외(레거시) /hq/* 사용
const HQ_BASE =
  typeof window !== 'undefined' && window.location.hostname === 'hq.newattend.com'
    ? ''
    : '/hq';

const HqLayout: React.FC = () => {
  const navigate = useNavigate();
  const user = hqAuthService.getCachedUser();
  const [menuOpen, setMenuOpen] = useState(false);

  // 보안 강화(2026-07-04): 무동작 30분 시 자동 로그아웃 (공용 PC 세션 방치 방지)
  useHqIdleLogout();

  // 오류 게시판 미확인(접수) 개수 배지 — 1분마다 갱신
  const [errorOpenCount, setErrorOpenCount] = useState(0);
  // 문의 답변 대기 매장 수 배지 (2026-07-27)
  const [supportWaiting, setSupportWaiting] = useState(0);
  // 승인 기다리는 점주 가입 신청 수 배지 (2026-08-20)
  const [signupPending, setSignupPending] = useState(0);
  // 홈페이지 상담 신청 새 건 배지 (2026-10-27, 대표 전용)
  const [consultNew, setConsultNew] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (user?.role === 'hq_admin') {
        hqConsultationService
          .newCount()
          .then((n) => { if (alive) setConsultNew(n); })
          .catch(() => {});
      }
      hqErrorReportService
        .summary()
        .then((s) => { if (alive) setErrorOpenCount(s.open_count); })
        .catch(() => {});
      hqSupportService
        .waitingCount()
        .then((n) => { if (alive) setSupportWaiting(n); })
        .catch(() => {});
      hqSignupService
        .pendingCount()
        .then((n) => { if (alive) setSignupPending(n); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('hq-consultations-changed', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('hq-consultations-changed', load); };
  }, []);

  const handleLogout = () => {
    hqAuthService.logout();
    navigate(`${HQ_BASE}/login`);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `block px-4 py-3 lg:py-2 rounded-lg transition-colors ${
      isActive
        ? 'bg-slate-700 text-white font-semibold'
        : 'text-slate-300 hover:bg-slate-800'
    }`;

  return (
    <div className="min-h-screen lg:flex bg-slate-50">
      {/* 모바일 상단 바 — 폰에서만 보임 */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-slate-900 text-white">
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="메뉴 열기"
          className="p-2 -ml-2 rounded hover:bg-slate-800"
        >
          <span className="block w-5 h-0.5 bg-white mb-1" />
          <span className="block w-5 h-0.5 bg-white mb-1" />
          <span className="block w-5 h-0.5 bg-white" />
        </button>
        <div className="font-bold">🏢 NewAttend HQ</div>
        {(errorOpenCount > 0 || supportWaiting > 0 || signupPending > 0) && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-xs font-bold">
            {errorOpenCount + supportWaiting + signupPending}
          </span>
        )}
      </div>

      {/* 서랍 배경 — 폰에서 메뉴 열렸을 때만 */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* 사이드바 — 폰에서는 서랍, PC에서는 고정 */}
      <aside
        onClick={() => setMenuOpen(false)}
        className={`w-64 bg-slate-900 text-white flex flex-col
          fixed inset-y-0 left-0 z-50 overflow-y-auto transition-transform lg:transition-none
          lg:static lg:z-auto lg:translate-x-0
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="px-6 py-6 border-b border-slate-700">
          <div className="text-xl font-bold">🏢 NewAttend HQ</div>
          <div className="text-xs text-slate-400 mt-1">{user?.brand_name || '본부'}</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavLink to={`${HQ_BASE}/dashboard`} className={navLinkClass}>
            📊 대시보드
          </NavLink>
          <NavLink to={`${HQ_BASE}/stores`} className={navLinkClass}>
            🏪 구독 독서실
          </NavLink>
          <NavLink to={`${HQ_BASE}/signups`} className={navLinkClass}>
            <span className="flex items-center justify-between">
              <span>✅ 가입 승인</span>
              {signupPending > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold">
                  {signupPending}
                </span>
              )}
            </span>
          </NavLink>
          {user?.role === 'hq_admin' && (
            <NavLink to={`${HQ_BASE}/consultations`} className={navLinkClass}>
              <span className="flex items-center justify-between">
                <span>📞 상담 신청</span>
                {consultNew > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                    {consultNew}
                  </span>
                )}
              </span>
            </NavLink>
          )}
          {user?.role === 'hq_admin' && (
            <NavLink to={`${HQ_BASE}/revenue`} className={navLinkClass}>
              💰 점포별 매출
            </NavLink>
          )}
          {user?.role === 'hq_admin' && (
            <NavLink to={`${HQ_BASE}/expiry`} className={navLinkClass}>
              ⏳ 만료 임박
            </NavLink>
          )}
          {user?.role === 'hq_admin' && (
            <NavLink to={`${HQ_BASE}/subscription-consents`} className={navLinkClass}>
              📝 구독 동의 현황
            </NavLink>
          )}
          {user?.role === 'hq_admin' && (
            <NavLink to={`${HQ_BASE}/alimtalk-bills`} className={navLinkClass}>
              🧾 알림톡 사용료
            </NavLink>
          )}
          <NavLink to={`${HQ_BASE}/plans`} className={navLinkClass}>
            💳 플랜 관리
          </NavLink>
          <NavLink to={`${HQ_BASE}/gates`} className={navLinkClass}>
            🚪 게이트 관리
          </NavLink>
          {user?.role === 'hq_admin' && (
            <NavLink to={`${HQ_BASE}/tablet-gates`} className={navLinkClass}>
              📡 태블릿·게이트
            </NavLink>
          )}
          <NavLink to={`${HQ_BASE}/notices`} className={navLinkClass}>
            📢 공지 관리
          </NavLink>
          <NavLink to={`${HQ_BASE}/support`} className={navLinkClass}>
            <span className="flex items-center justify-between">
              <span>💬 문의</span>
              {supportWaiting > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold">
                  {supportWaiting}
                </span>
              )}
            </span>
          </NavLink>
          <NavLink to={`${HQ_BASE}/error-reports`} className={navLinkClass}>
            <span className="flex items-center justify-between">
              <span>🐛 오류 게시판</span>
              {errorOpenCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-xs font-bold">
                  {errorOpenCount}
                </span>
              )}
            </span>
          </NavLink>
          {/* 입시 컨설팅 메뉴 — 구독형 운영 콘솔에서는 숨김 (코드 보존, 추후 재활성 가능)
          <div className="mt-3 pt-3 border-t border-slate-700/50">
            <div className="px-3 py-1 text-xs text-slate-400 font-semibold uppercase">
              입시 컨설팅
            </div>
          </div>
          <NavLink to={`${HQ_BASE}/admission/match`} className={navLinkClass}>
            🎯 학생 매칭
          </NavLink>
          <NavLink to={`${HQ_BASE}/admission/insights`} className={navLinkClass}>
            📈 5년 인사이트
          </NavLink>
          */}
          <div className="mt-3 pt-3 border-t border-slate-700/50" />
          <NavLink to={`${HQ_BASE}/staff`} className={navLinkClass}>
            👥 직원 관리
          </NavLink>
          <NavLink to={`${HQ_BASE}/brand`} className={navLinkClass}>
            🏢 본부 정보
          </NavLink>
        </nav>

        <div className="px-4 py-4 border-t border-slate-700">
          <div className="text-sm font-medium">{user?.name || '직원'}</div>
          <div className="text-xs text-slate-400">{user?.email}</div>
          <div className="text-xs text-slate-500 mt-1">
            {user?.role === 'hq_admin' ? '관리자' : '일반 직원'}
          </div>
          <button
            onClick={handleLogout}
            className="mt-3 w-full px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded transition-colors"
          >
            로그아웃
          </button>
        </div>
      </aside>

      {/* 메인 */}
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="max-w-6xl mx-auto px-4 py-5 sm:px-6 sm:py-8">
          <Outlet />
        </div>
      </main>

      {/* 운영 매뉴얼 챗봇 (우하단 플로팅) */}
      <ChatWidget />
    </div>
  );
};

export default HqLayout;
